import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { theme, useResponsive } from '@sg/garnish';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import type { Asset, Entry } from 'contentful';

/**
 * Returns the Contentful data for the gift card account header.
 */
export const useGiftCardContent = () => {
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const contentfulResponse = useContentfulContentTypeEntry<GiftCardsScreen>({
    contentType: 'giftCardsScreen',
  });

  return useMemo(() => {
    const contentfulData = contentfulResponse.data?.fields;
    const header = currentBreakpoint.isXS
      ? contentfulData?.giftCardAccountCtaXs?.fields
      : contentfulData?.giftCardAccountCta?.fields;

    return {
      isFetchingContent: contentfulResponse.fetching,
      headerTitle: header?.title ?? formatMessage(messages.headerTitle),
      headerImage: header?.image?.fields?.file?.url,
      headerImageA11y: header?.image?.fields?.description,
      headerTextColor: header?.textColor ?? theme.colors.KALE,
      headerBackgroundColor: header?.backgroundColor ?? theme.colors.OPACITY.KALE.LIGHTEST, // prettier-ignore
    };
  }, [contentfulResponse.data, contentfulResponse.fetching, formatMessage, currentBreakpoint]);
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  headerTitle: {
    defaultMessage: 'Gift the gift of green. Buy a gift card.',
    description: 'Gift Cards Screen | Fallbacks | Header Title',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardsScreen = {
  giftCardAccountCta: Entry<{
    title: string;
    textColor: string;
    backgroundColor: string;
    image: Asset;
  }>;
  giftCardAccountCtaXs: Entry<{
    title: string;
    textColor: string;
    backgroundColor: string;
    image: Asset;
  }>;
};
